import axios, { type AxiosResponse } from 'axios'

import agent from 'agent'
import {
  type ChooseCompanyAuthResponse,
  type PwdAuthResponse
} from 'types/api/user/session'
import { appConfig } from 'utils/app-config'
import { getBaseUrl } from 'utils/baseUrls'

import type { UserSession } from 'types/api/users/sessions/user-session'

interface GoogleAuthResponse {
  access_type: string
  aud: string
  azp: string
  email: string
  email_verified: string
  exp: string
  expires_in: string
  scope: string
  sub: string
}

const urlEncodedHeaders = {
  'Content-Type': 'application/x-www-form-urlencoded'
}

export default {
  async signin({
    email,
    password,
    companyName,
    token
  }: {
    email: string
    password: string
    companyName?: string
    token?: string
  }) {
    const endpoint = token ? '/users/auth/mfa/auth' : '/users/auth'
    const params = { getCompanySession: true }
    const data = new URLSearchParams({
      email,
      password,
      // Company name is not available for new users on initial login
      ...(companyName && { companyName }),
      ...(token && { token })
    })

    return agent
      .post<PwdAuthResponse>(endpoint, data, { params })
      .then(({ data }) => data)
  },

  async signInWithGoogle(params: {
    accessToken: string
    companyName?: string
  }) {
    const { GOOGLE_OAUTH_TOKENINFO_URL } = appConfig.getState()

    return axios
      .get(GOOGLE_OAUTH_TOKENINFO_URL, {
        params: { access_token: params.accessToken }
      })
      .then(validateGoogleAuthResponse)
      .then((response) => authenticateUserViaSSO({ ...response, ...params }))
  },

  requestPasswordReset(email: string) {
    return agent.post(
      '/users/auth/password/reset',
      new URLSearchParams({ email }),
      { headers: urlEncodedHeaders }
    )
  },

  resetPassword(code: string, email: string, password: string) {
    return agent.post(
      '/users/auth/password',
      new URLSearchParams({ code, email, password })
    )
  },

  internalResetPassword(currentPassword: string, newPassword: string) {
    return agent.put(
      '/users/auth/password',
      new URLSearchParams({ currentPassword, newPassword })
    )
  },

  findCompany(email: string) {
    return agent.get('/users/auth/emailCompanies', {
      params: { email },
      headers: urlEncodedHeaders
    })
  },

  async startCompanyContext({
    email,
    companyName
  }: {
    email: string
    companyName: string
  }) {
    return agent
      .get('/users/auth/methods', { params: { email, companyName } })
      .then(({ data }) => data)
  },

  async validateMFA({ email, token }: { email: string; token: string }) {
    return agent
      .post('/users/auth/mfa/validate', new URLSearchParams({ email, token }), {
        headers: urlEncodedHeaders
      })
      .then(({ data }) => data)
  },

  generateGoogleUrl(params: { nextPathname: string }) {
    return createGoogleAuthenticationUrl('/users/sso/google/login', params)
  },

  generateSamlUrl({
    companyName,
    nextPathname = '/'
  }: {
    companyName: string
    nextPathname?: string
  }) {
    const params = new URLSearchParams({
      getCompanySession: 'true',
      destination: nextPathname,
      acsHandler: `${location.origin}/users/sso/saml/acs`,
      ...(companyName && { companyName })
    })

    return `${getBaseUrl('api')}/v1/users/auth/saml/login?${params.toString()}`
  }
}

function createGoogleAuthenticationUrl(
  url: string,
  state: Record<string, string>
) {
  const { GOOGLE_OAUTH_CLIENT_ID, GOOGLE_OAUTH_SCOPE, GOOGLE_OAUTH_URL } =
    appConfig.getState()

  const params = new URLSearchParams({
    scope: GOOGLE_OAUTH_SCOPE.replace(/,/g, ' '),
    redirect_uri: `${location.origin}${url}`,
    response_type: 'token',
    client_id: GOOGLE_OAUTH_CLIENT_ID,
    state: JSON.stringify(state)
  })

  return `${GOOGLE_OAUTH_URL}?${params.toString()}`
}

function validateGoogleAuthResponse({
  data
}: AxiosResponse<GoogleAuthResponse>) {
  const { GOOGLE_OAUTH_CLIENT_ID } = appConfig.getState()

  if (data.aud !== GOOGLE_OAUTH_CLIENT_ID)
    return Promise.reject('invalid-token')

  return Promise.resolve(data)
}

async function authenticateUserViaSSO({
  accessToken,
  companyName,
  email
}: GoogleAuthResponse & { accessToken: string; companyName?: string }) {
  const response = await agent.post<ChooseCompanyAuthResponse | UserSession>(
    '/users/auth/sso?getCompanySession=true',
    new URLSearchParams({
      accessToken,
      email,
      provider: 'google',
      ...(companyName && { companyName })
    }),
    { headers: urlEncodedHeaders }
  )

  return response.data
}
